import { z } from 'zod';
import type { RangesKind, RangeRow, StateReferenceFixedClass } from '../types/parameters.types';
import {
	isFiniteNumber,
	validateClassLimits,
	validateDrisValues,
	validateFoliarValues,
} from '../utils/validation';

const requiredText = (message: string) => z.string().trim().min(1, message);

const numberValue = (message: string, max?: number) =>
	z.custom<number>((value) => isFiniteNumber(value) && value >= 0 && (max === undefined || value <= max), {
		message,
	});

export const RegionFormSchema = z.object({
	name: requiredText('Informe o nome da região.'),
	stateCodes: z.array(z.string()).min(1, 'Selecione ao menos um estado.'),
});

export const SoilRuleSchema = z
	.object({
		kind: z.enum(['fixed', 'Prem', 'clay']),
		usesBand: z.boolean(),
		name: requiredText('Informe o nome.'),
		method: requiredText('Informe o método.'),
		unit: requiredText('Informe a unidade.'),
		idealRange: requiredText('Informe a faixa ideal.'),
		minValue: z.number().min(0, 'Use um valor a partir de zero.').optional(),
		maxValue: z.number().min(0, 'Use um valor a partir de zero.').optional(),
		classes: z.custom<StateReferenceFixedClass[]>(),
	})
	.superRefine((data, ctx) => {
		const limitsError = validateClassLimits(data.classes);
		if (limitsError) ctx.addIssue({ code: 'custom', path: ['classes'], message: limitsError });
		if (!data.usesBand) return;
		if (data.minValue === undefined && data.maxValue === undefined) {
			ctx.addIssue({ code: 'custom', path: ['minValue'], message: 'Informe ao menos um limite da faixa.' });
		}
		if (data.minValue !== undefined && data.maxValue !== undefined && data.minValue >= data.maxValue) {
			ctx.addIssue({ code: 'custom', path: ['maxValue'], message: 'O máximo deve ser maior que o mínimo.' });
		}
	});

export const buildValuesSchema = (max?: number, maxMessage?: string) =>
	z.record(z.string(), numberValue(maxMessage ?? 'Informe um valor a partir de zero.', max));

export const buildRangeRowsSchema = (kind: RangesKind) => {
	const validate = kind === 'foliar' ? validateFoliarValues : validateDrisValues;
	return z.custom<RangeRow[]>().superRefine((rows, ctx) => {
		for (const row of rows) {
			const error = validate(row.values);
			if (error) {
				ctx.addIssue({ code: 'custom', message: `${row.code.replace('_', ' / ')}: ${error}` });
				return;
			}
		}
	});
};

export const PlanGeneralSchema = z.object({
	name: requiredText('Informe o nome.'),
	version: z.custom<number>((value) => Number.isInteger(value) && (value as number) >= 1, {
		message: 'Use um número inteiro a partir de 1.',
	}),
	description: z.string(),
});

export const IncompleteParamsSchema = z.array(
	z.object({
		method: requiredText('Informe o método.'),
		unit: requiredText('Informe a unidade.'),
		idealRange: requiredText('Informe a faixa ideal.').nullable(),
	})
);

export type PlanGeneralForm = z.infer<typeof PlanGeneralSchema>;
